<template>
  <main
    class="flex flex-col min-h-[calc(100vh-200px)] items-center justify-center px-16 py-32"
    aria-labelledby="verify-email-heading"
  >
    <div
      data-testid="verify-email-card"
      class="w-full max-w-[480px] mx-auto bg-foreground px-16 py-24 sm:rounded-xl sm:box-shadow-static-lg"
    >
      <VerifyEmailWaiting
        heading-id="verify-email-heading"
        :account-email="account?.email || ''"
        :show-pending-notices="true"
        @verified="onVerified"
      />
      <div v-if="showResumeCta" class="mt-32 flex justify-center">
        <Button
          data-testid="verify-email-resume"
          :value="resumeLabel"
          size="medium"
          class="w-full sm:w-auto"
          @click="continueAfterVerification"
        />
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import VerifyEmailWaiting from '@/apps/verifyEmail/VerifyEmailWaiting.vue';
import Button from '@/components/button/Button.vue';
import { redirect } from '@/helpers/redirects';
import {
  resolvePostVerifyDestination,
  resolvePostVerifyResumeLabel,
  currentUserIsParent,
  currentUserIsSitter,
  isSafeInternalPath,
  isVerifyEmailPath,
} from '@/apps/verifyEmail/helpers/paths';
import {
  clearEmailVerificationRequired,
  getPendingEmailVerificationReturnPath,
  isEmailVerificationPending,
  markEmailVerificationRequired,
} from '@/apps/verifyEmail/helpers/pendingEmailVerification';
import { getEmailVerificationPlacement, PLACEMENT_NO_VERIFICATION_REQUIRED } from '@/apps/verifyEmail/helpers/placementPolicy';
import {
  getParentEmailVerificationPlacement,
  PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED,
} from '@/apps/verifyEmail/helpers/parentPlacementPolicy';
import type { Account } from '@/models/account';

defineOptions({ name: 'VerifyEmailPage' });

const store = useStore();
const instance = getCurrentInstance();

const account = computed((): Account | null => store.getters['account/getAccount'] || null);
const showResumeCta = ref(false);

const postVerifyDestination = computed(() =>
  resolvePostVerifyDestination(routeQuery(), currentUserIsSitter(), getPendingEmailVerificationReturnPath())
);

const resumeLabel = computed(() => resolvePostVerifyResumeLabel(postVerifyDestination.value));

onMounted(() => {
  armStickyTrapFromRoute();
});

function routeQuery(): Record<string, unknown> {
  const route = (instance?.proxy as { $route?: { query?: Record<string, unknown> } } | null)?.$route;
  return route?.query ?? {};
}

/**
 * Refresh an existing checkpoint's safe return path on mount.
 * A next query alone is not proof of a gate and must not trap Variant B.
 */
function armStickyTrapFromRoute(): void {
  const isSitter = currentUserIsSitter();
  const isParent = currentUserIsParent();
  if (!isSitter && !isParent) {
    return;
  }

  if (!isEmailVerificationPending()) {
    return;
  }

  if (isSitter && getEmailVerificationPlacement() === PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return;
  }

  if (isParent && getParentEmailVerificationPlacement() === PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return;
  }

  const rawNext = routeQuery().next;
  const candidate = Array.isArray(rawNext) ? rawNext[0] : rawNext;
  const returnPath =
    typeof candidate === 'string' && isSafeInternalPath(candidate) && !isVerifyEmailPath(candidate)
      ? candidate
      : getPendingEmailVerificationReturnPath();

  markEmailVerificationRequired(returnPath);
}

function onVerified(): void {
  showResumeCta.value = true;
}

function continueAfterVerification(): void {
  const pendingReturnPath = getPendingEmailVerificationReturnPath();
  clearEmailVerificationRequired();
  redirect(resolvePostVerifyDestination(routeQuery(), currentUserIsSitter(), pendingReturnPath));
}
</script>
